import React, { useState, useEffect, useCallback } from 'react';
import {
  Key, Shield, Phone, Mail, CheckCircle, XCircle, AlertTriangle, Save,
  Target, Globe, Users, Bug, FileText, Loader2, Search, Bot, GitBranch,
  RefreshCw, Server, Calendar, Clock, Hash, Zap, Info
} from 'lucide-react';

// ─── API helper ───────────────────────────────────────────────────────────────
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const res = await fetch(`/api/v1${path}`, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
  return data;
};

// ─── Constantes ───────────────────────────────────────────────────────────────
const MODULE_META = {
  scan_interne:    { label: 'Scan Interne',                  icon: Target,     desc: 'Scan des infrastructures internes (IP, ports, services)' },
  scan_externe:    { label: 'Scan Externe',                  icon: Globe,      desc: 'Scan des domaines et services exposés sur Internet' },
  active_directory:{ label: 'Active Directory / Pentest AD', icon: Users,      desc: 'Kerberoasting, LDAP enum, DCSync, BloodHound' },
  pentest:         { label: 'Pentest & Exploitation',        icon: Shield,     desc: 'Exploitation automatique via Metasploit, shells, pivoting' },
  vulnerabilities: { label: 'Vulnérabilités & CVE',          icon: Bug,        desc: 'Détection CVE, scoring CVSS, recommandations OWASP' },
  reports:         { label: 'Rapports PDF / Word',           icon: FileText,   desc: 'Génération rapports executive, technique, compliance' },
  credentials:     { label: 'Credentials & OSINT Breach',   icon: Search,     desc: 'Recherche de credentials compromis, Dark Web' },
  ai_assistant:    { label: 'Assistant IA',                  icon: Bot,        desc: 'IA conversationnelle, analyse de résultats, recommandations' },
  osint:           { label: 'OSINT (DNS, Subdomains…)',      icon: Server,     desc: 'Reconnaissance passive, énumération DNS, WHOIS' },
  sankey_analysis: { label: 'Analyse Sankey / Kill Chain',  icon: GitBranch,  desc: 'Visualisation des chemins d\'attaque et kill chain' },
  updates:         { label: 'Gestion des mises à jour',     icon: RefreshCw,  desc: 'Mise à jour des modules et signatures de vulnérabilités' },
};

const TIER_COLORS = {
  Starter:      'text-blue-400 bg-blue-500/20 border-blue-500/50',
  Professional: 'text-purple-400 bg-purple-500/20 border-purple-500/50',
  Enterprise:   'text-brand-green bg-brand-green/20 border-brand-green/50',
  Custom:       'text-orange-400 bg-orange-500/20 border-orange-500/50',
};

const fmtLimit = (val) => (val === -1 || val === undefined || val === null) ? '∞ Illimité' : val.toLocaleString('fr-FR');
const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

// ─── Composant principal ──────────────────────────────────────────────────────
const LicenseSettings = () => {
  const [tokenInput, setTokenInput]       = useState('');
  const [licenseData, setLicenseData]     = useState(null);
  const [profile, setProfile]             = useState(null);
  const [isActivating, setIsActivating]   = useState(false);
  const [error, setError]                 = useState('');
  const [successMsg, setSuccessMsg]       = useState('');

  // Charger profil + licence sauvegardée au démarrage
  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileData, settingsData] = await Promise.all([
          apiFetch('/account/profile'),
          apiFetch('/user-settings'),
        ]);
        setProfile(profileData);
        if (settingsData?.license_settings?.license_id) {
          setLicenseData(settingsData.license_settings);
        }
      } catch {
        // Fallback localStorage
        const saved = localStorage.getItem('sylink_license');
        if (saved) {
          try { setLicenseData(JSON.parse(saved)); } catch { /* ignore */ }
        }
      }
    };
    loadData();
  }, []);

  // Valider et activer le token via le backend
  const handleActivate = useCallback(async () => {
    const token = tokenInput.trim();
    if (!token) return;

    setIsActivating(true);
    setError('');
    setSuccessMsg('');

    try {
      const result = await apiFetch('/user-settings/license/validate', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      setLicenseData(result.license);
      localStorage.setItem('sylink_license', JSON.stringify(result.license));
      setTokenInput('');
      setSuccessMsg(result.message || 'Licence activée avec succès.');
    } catch (err) {
      setError(err.message || 'Erreur lors de la validation de la licence.');
    } finally {
      setIsActivating(false);
    }
  }, [tokenInput]);

  // ── Calculs expiration ──────────────────────────────────────────────────────
  const expiresAt       = licenseData?.expires_at ? new Date(licenseData.expires_at) : null;
  const now             = new Date();
  const isExpired       = expiresAt ? now > expiresAt : false;
  const daysRemaining   = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 86400000)) : null;
  const expiringSoon    = daysRemaining !== null && daysRemaining < 30 && !isExpired;

  const limits   = licenseData?.limits   || {};
  const modules  = licenseData?.modules  || {};
  const customer = licenseData?.customer || {};

  // Données du compte réel (font foi sur celles du token)
  const accountCompany  = customer.account_company  || profile?.company?.name  || customer.company || '—';
  const accountUser     = customer.account_user     || (profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '') || customer.name || '—';
  const accountEmail    = customer.account_email    || profile?.email           || customer.email  || '—';

  // Nom de la société dans le compte (pour vérification avant activation)
  const profileCompanyName = profile?.company?.name || '';
  const profileUserName    = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
  const profileEmail       = profile?.email || '';

  const statusBadge = isExpired
    ? 'bg-red-500/20 border-red-500/50 text-red-400'
    : expiringSoon
    ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
    : 'bg-green-500/20 border-green-500/50 text-green-400';
  const statusLabel = isExpired ? 'Expirée' : expiringSoon ? `Active — ${daysRemaining}j restants` : 'Active';

  return (
    <div className="space-y-6">

      {/* ── Contact Commercial ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand-green/20 to-blue-500/20 border border-brand-green/50 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="bg-brand-green/30 p-3 rounded-xl">
            <Key className="w-8 h-8 text-brand-green" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">Gestion de Licence SYLink</h3>
            <p className="text-gray-300 mb-4">
              Votre licence SYLink contrôle les modules activés, les quotas mensuels et la durée d'accès.
              Pour toute mise à niveau, renouvellement ou licence personnalisée, contactez notre équipe commerciale.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="mailto:contact@sylink.fr"
                className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors text-sm">
                <Mail className="w-4 h-4" /> contact@sylink.fr
              </a>
              <a href="tel:+33415540000"
                className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors text-sm">
                <Phone className="w-4 h-4" /> +33 4 15 54 00 00
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Saisie Token ───────────────────────────────────────────────────── */}
      <div className="bg-brand-black rounded-xl border border-gray-800 p-6">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <Key className="w-5 h-5 text-brand-green" />
          Activer une Licence
        </h3>
        <p className="text-gray-500 text-sm mb-4">
          Collez le token de licence fourni par SYLink Security (format: <span className="font-mono text-gray-400">SYLINK.xxxxx.xxxxx</span>)
        </p>

        {/* Bandeau société du compte courant */}
        {profile && (
          <div className="flex items-start gap-3 px-4 py-3 bg-brand-black-lighter border border-gray-700 rounded-lg mb-4">
            <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-400 leading-snug">
              <span className="text-gray-300">Compte actuel : </span>
              <span className="text-white font-semibold">{profileUserName || '—'}</span>
              {profileCompanyName && (
                <> &nbsp;·&nbsp; <span className="text-brand-green font-semibold">{profileCompanyName}</span></>
              )}
              {profileEmail && (
                <> &nbsp;·&nbsp; <span className="text-gray-500">{profileEmail}</span></>
              )}
              {!profileCompanyName && (
                <span className="ml-2 text-yellow-500 text-xs">
                  ⚠ Aucune société renseignée — requis pour activer une licence
                  (<a href="/account?tab=profile" className="underline hover:text-yellow-300">Compléter le profil</a>)
                </span>
              )}
            </div>
          </div>
        )}

        <textarea
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="SYLINK.eyJsaWNlbnNlX2lkIjoiU1lMLTIwMjYtQUJDREVGIi..."
          rows={4}
          className="w-full px-4 py-3 bg-brand-black-lighter border border-gray-700 rounded-lg text-white font-mono text-xs placeholder-gray-600 focus:border-brand-green focus:outline-none transition-colors resize-none mb-3"
        />

        {error && (
          <div className="flex items-start gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm mb-3">
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="flex items-start gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm mb-3">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{successMsg}</span>
          </div>
        )}

        <button
          onClick={handleActivate}
          disabled={isActivating || !tokenInput.trim()}
          className="px-6 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isActivating ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Validation en cours...</>
          ) : (
            <><Save className="w-5 h-5" /> Activer la licence</>
          )}
        </button>
      </div>

      {/* ── Licence Active ─────────────────────────────────────────────────── */}
      {licenseData && (
        <>
          {/* Avertissement expiration */}
          {(isExpired || expiringSoon) && (
            <div className={`border rounded-xl p-5 flex items-start gap-4 ${isExpired ? 'bg-red-500/10 border-red-500/50' : 'bg-yellow-500/10 border-yellow-500/50'}`}>
              <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${isExpired ? 'text-red-400' : 'text-yellow-400'}`} />
              <div>
                <h4 className={`font-semibold mb-1 ${isExpired ? 'text-red-400' : 'text-yellow-400'}`}>
                  {isExpired ? 'Licence expirée' : `Licence expire dans ${daysRemaining} jour(s)`}
                </h4>
                <p className="text-gray-300 text-sm">
                  {isExpired
                    ? `Votre licence a expiré le ${fmtDate(licenseData.expires_at)}. L'accès aux fonctionnalités est limité.`
                    : `Votre licence expire le ${fmtDate(licenseData.expires_at)}. Renouvelez dès maintenant pour éviter toute interruption.`}
                </p>
                <a href="mailto:contact@sylink.fr?subject=Renouvellement%20Licence%20SYLink"
                  className={`inline-flex items-center gap-2 mt-3 px-4 py-2 border rounded-lg text-sm transition-colors ${isExpired ? 'bg-red-500/20 text-red-400 border-red-500/50 hover:bg-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/30'}`}>
                  <Mail className="w-4 h-4" /> Renouveler ma licence
                </a>
              </div>
            </div>
          )}

          {/* Carte principale */}
          <div className="bg-brand-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-green" />
                Licence Active
              </h3>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${TIER_COLORS[licenseData.tier] || TIER_COLORS.Custom}`}>
                  {licenseData.tier}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusBadge}`}>
                  {statusLabel}
                </span>
              </div>
            </div>

            {/* Infos client / dates — données du compte réel */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-brand-black-lighter rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Hash className="w-3 h-3" /> ID Licence
                </p>
                <p className="font-mono text-brand-green font-semibold text-sm">{licenseData.license_id}</p>
              </div>
              <div className="bg-brand-black-lighter rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Expiration
                </p>
                <p className={`font-semibold text-sm ${isExpired ? 'text-red-400' : expiringSoon ? 'text-yellow-400' : 'text-white'}`}>
                  {fmtDate(licenseData.expires_at)}
                </p>
              </div>
              <div className="bg-brand-black-lighter rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Support
                </p>
                <p className="text-white font-semibold text-sm capitalize">{licenseData.support_level || '—'}</p>
              </div>
            </div>

            {/* Titulaire de la licence — données du compte (font foi) */}
            <div className="bg-brand-black-lighter border border-gray-700 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Titulaire de la licence
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Société</p>
                  <p className="text-white font-semibold">{accountCompany}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Responsable</p>
                  <p className="text-white font-semibold">{accountUser || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Email</p>
                  <p className="text-gray-300 text-sm">{accountEmail}</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Ces informations proviennent de votre profil.
                <a href="/account?tab=profile" className="text-brand-green hover:underline ml-1">
                  Modifier dans Compte › Profil
                </a>
              </p>
            </div>

            {/* Quotas mensuels */}
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-brand-green" /> Quotas mensuels
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { key: 'max_users',                              label: 'Utilisateurs',      icon: Users },
                { key: 'max_scans_per_month',                    label: 'Scans réseau',       icon: Target },
                { key: 'max_ad_pentests_per_month',              label: 'Pentests AD',        icon: Shield },
                { key: 'max_credentials_searches_per_month',     label: 'Credentials',        icon: Search },
                { key: 'max_reports_per_month',                  label: 'Rapports',           icon: FileText },
                { key: 'max_ips_per_scan',                       label: 'IPs / scan',         icon: Server },
                { key: 'max_ad_forests',                         label: 'Forêts AD',          icon: GitBranch },
                { key: 'max_workspaces',                         label: 'Workspaces',         icon: Globe },
              ].map(({ key, label, icon: Icon }) => {
                const val = limits[key];
                const isUnlimited = val === -1 || val === undefined || val === null;
                return (
                  <div key={key} className="bg-brand-black-lighter rounded-lg p-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Icon className="w-3.5 h-3.5 text-gray-500" />
                      <p className="text-xs text-gray-500">{label}</p>
                    </div>
                    <p className={`font-bold text-lg ${isUnlimited ? 'text-brand-green' : 'text-white'}`}>
                      {isUnlimited ? '∞' : val?.toLocaleString('fr-FR')}
                    </p>
                    {isUnlimited && <p className="text-xs text-brand-green/70">Illimité</p>}
                  </div>
                );
              })}
            </div>

            {/* Modules */}
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-brand-green" /> Modules inclus
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(MODULE_META).map(([key, { label, icon: Icon, desc }]) => {
                const enabled = modules[key] === true;
                return (
                  <div key={key}
                    className={`rounded-lg border p-4 transition-all ${enabled ? 'bg-brand-green/10 border-brand-green/40' : 'bg-gray-800/30 border-gray-700/50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg flex-shrink-0 ${enabled ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700 text-gray-600'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h5 className={`font-semibold text-sm ${enabled ? 'text-white' : 'text-gray-600'}`}>{label}</h5>
                          <p className={`text-xs mt-0.5 ${enabled ? 'text-gray-400' : 'text-gray-700'}`}>{desc}</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${enabled ? 'text-green-400' : 'text-gray-600'}`}>
                        {enabled
                          ? <><CheckCircle className="w-4 h-4" /> <span>Inclus</span></>
                          : <><XCircle className="w-4 h-4" /> <span>Non inclus</span></>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── Info tiers ─────────────────────────────────────────────────────── */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-400">
            <p className="font-semibold text-blue-400 mb-2">Offres de licence disponibles</p>
            <ul className="space-y-1.5">
              <li><span className="text-blue-400 font-semibold">Starter</span> — 30 scans/mois, 3 utilisateurs, modules basiques (scan interne + rapports)</li>
              <li><span className="text-purple-400 font-semibold">Professional</span> — 200 scans/mois, 10 utilisateurs, AD + credentials + IA inclus</li>
              <li><span className="text-brand-green font-semibold">Enterprise</span> — Tout illimité, tous modules, support prioritaire</li>
              <li><span className="text-orange-400 font-semibold">Custom</span> — Quotas sur mesure selon vos besoins spécifiques</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseSettings;
